import React from 'react';
import { ProgressOverlay } from '../../common/progress-overlay/ProgressOverlay';
import styles from './DocumentDetails.module.scss';
import { Box, Button, Grid, IconButton, Tab, Tabs, Tooltip, Typography } from '@mui/material';
import AISummaryDetails from './components/ai-summary-details/AISummaryDetails';
import DocumentContent from './components/document-content/DocumentContent';
import DocumentDetailsBlock from './components/document-details/DocumentDetailsBlock';
import { useDocumentItem } from './hooks/useDocumentItem';
import { useTranslation } from 'react-i18next';
import { Link, useParams, useSearchParams, useLocation } from 'react-router-dom';
import { ROUTES } from '../../common/constants/routes';
import { useDocumentDetails } from './hooks/useDocumentDetails';
import { useAppSelector } from '../../common/hooks/useAppSelector';
import { useDocumentHTML } from './hooks/useDocumentHTML';
import { useDocumentSigning } from '../document-vault/hooks/useDocumentSigning';
import MessageModal from '../../common/message-modal/MessageModal';
import FileTypeDownloadModal from '../../common/file-type-download-modal/FileTypeDownloadModal';
import { useDeleteMessageModal } from '../document-vault/hooks/useDeleteMessageModal';
import { useFileTypeDownload } from '../../common/file-type-download-modal/useFileTypeDownload';
import { useTemplateCreation } from './hooks/useTemplateCreation';
import { useLatestVersion } from './hooks/useLatestVersion';
import BrushIcon from '../../../static/assets/svg/brush.svg';
import ProgressBarFancy from '../../common/fancy-progress-bar';
import { useDocumentsFunctions } from '../document-vault/hooks/useDocumentsFunctions';

const DocumentDetails = () => {
  const { t } = useTranslation();
  const { activeTab, handleTabChange } = useDocumentItem();
  const { documentId } = useParams();
  const [params] = useSearchParams();
  const contractGenerated = params.get('contractGenerated');
  const generateTemplateParam = params.get('generateTemplate');
  const backURL = params.get('backURL');
  const location = useLocation();
  const path = location.pathname;
  const { checkedLatestVersion } = useLatestVersion();
  const employeesStatus = useAppSelector((state) => state.employees.status);
  const suppliersStatus = useAppSelector((state) => state.suppliers.status);
  const customersStatus = useAppSelector((state) => state.customers.status);
  const businessContactsStatus = useAppSelector((state) => state.businessProfile.status);
  const detailsProps = {
    ...useDocumentDetails({
      id: Number(documentId),
      isDocumentContract: Boolean(contractGenerated),
      allowedCreatingHTML: activeTab === 1,
    }),
  };
  const { navigateToDocumentPageTab } = useDocumentsFunctions();
  const htmlArray = contractGenerated
    ? null
    : !detailsProps.documentUploadLatestHTMLId && detailsProps.documentDetails
    ? detailsProps.documentDetails.document_upload_html
    : detailsProps.documentUploadLatestHTMLId
    ? [detailsProps.documentUploadLatestHTMLId]
    : [];
  const {
    documentHTML,
    setDocumentHTML,
    saveDocumentHTML,
    pendingStatus: pendingStatusHTML,
    contentEditable,
    setContentEditable,
    showConfirmationModal,
    setShowConfirmationModal,
  } = useDocumentHTML(
    htmlArray,
    activeTab === 1,
    detailsProps.documentDetails && 'contract_generation' in detailsProps.documentDetails
      ? detailsProps.documentDetails.html
      : null
  );

  const dataFetching =
    detailsProps.loadingData ||
    suppliersStatus === 'loading' ||
    customersStatus === 'loading' ||
    employeesStatus === 'loading' ||
    businessContactsStatus === 'loading';

  const {
    document: documentToSign,
    setDocument: setDocumentToSign,
    dialogOpen: signingDialogOpen,
    setDialogOpen: setSigningDialogOpen,
    pendingStatus: pendingStatusSigning,
    handleSignOriginalDocument,
    handleSignLatestVersion,
  } = useDocumentSigning();
  const {
    setModalOpen: setDeleteMessageModalOpen,
    modalOpen: deleteMessageModalOpen,
    pendingStatus: documentDeletePendingStatus,
    documentToDelete,
    setDocumentToDelete,
    handleDeleteDocument,
  } = useDeleteMessageModal();
  const {
    setModalOpen: setFileTypeModalOpen,
    modalOpen: fileTypeModalOpen,
    handleCloseModal: handleCloseFileTypeModal,
    setDocument: setDocumentForDownloading,
    document: documentForDownloading,
    setFileName: setFileNameForDownloading,
    fileName: fileNameForDownloading,
    fileType: fileTypeForDownloading,
    setFileType: setFileTypeForDownloading,
    handleDownloadContract,
    downloadDocumentUploadItem,
    handleDownloadHTML,
    pendingStatus: downloadPendingStatus,
  } = useFileTypeDownload();
  const {
    templateCreationEnabled,
    setTemplateCreationEnabled,
    pendingStatus: templateCreationPending,
    template,
    creatingTemplate,
    setTemplate,
    setDocumentTemplate,
    deletePlaceholder,
    addTemplatePlaceholderRow,
  } = useTemplateCreation(
    Number(documentId),
    Boolean(contractGenerated),
    contractGenerated
      ? null
      : detailsProps?.documentDetails &&
        detailsProps?.documentDetails?.document_upload_html?.length > 0
      ? Math[checkedLatestVersion ? 'max' : 'min'](
          // eslint-disable-next-line no-unsafe-optional-chaining
          ...detailsProps?.documentDetails?.document_upload_html
        )
      : detailsProps?.documentUploadLatestHTMLId,
    detailsProps?.documentDetails && detailsProps?.documentDetails?.name
  );
  return (
    <>
      <Box marginBottom='16px'>
        <Grid container className={styles.header}>
          <Grid item xs={6}>
            <Typography variant='h5' component='h1' className={styles.title}>
              {path.includes(ROUTES.WORKSPACE)
                ? t('headers.workspace')
                : t('headers.documentVault')}
            </Typography>
            <Box className={styles.breadcrumbs}>
              {backURL ? (
                <Link className={styles.documentViewText} to={backURL}>
                  {t('buttons.back')}
                </Link>
              ) : (
                <>
                  <Link
                    className={styles.documentViewText}
                    to={path.includes(ROUTES.WORKSPACE) ? ROUTES.WORKSPACE : ROUTES.DOCUMENT_VAULT}
                  >
                    {path.includes(ROUTES.WORKSPACE)
                      ? t('headers.workspace')
                      : t('headers.documentVault')}
                  </Link>
                  <Typography variant='body2' className={styles.separator}>
                    ›
                  </Typography>
                  <Typography variant='body2' className={styles.documentViewText}>
                    {detailsProps.documentDetails ? detailsProps.documentDetails.name : ''}
                  </Typography>
                </>
              )}
            </Box>
          </Grid>
        </Grid>
        <Grid container justifyContent='space-between' spacing={2} className='inputs-editable'>
          {activeTab === 0 ? (
            <>
              <Grid
                item
                xs={4.9}
                className={`${styles.gridItem} ${
                  detailsProps.propertiesEditMode ? styles.card_border : ''
                }`}
                sx={{ ml: '16px', position: 'relative' }}
              >
                <Box
                  display='flex'
                  paddingBottom='8px'
                  justifyContent='space-between'
                  alignItems='flex-start'
                  gap='16px'
                >
                  <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    className='tabs-document-details'
                  >
                    <Tab
                      label={t('tabs.documentDetails')}
                      className={`${styles.tab} ${activeTab === 0 ? styles.activeTab : ''}`}
                    />
                    <Tab label={t('tabs.documentContent')} className={styles.tab} />
                  </Tabs>
                  {detailsProps.propertiesEditMode ? (
                    <Button
                      type='button'
                      className='btn-default btn-action-basic'
                      role='button'
                      variant='contained'
                      onClick={() => {
                        detailsProps.updateDocumentItem();
                        detailsProps.setPropertiesEditMode(false);
                      }}
                    >
                      {t('buttons.save')}
                    </Button>
                  ) : (
                    <Tooltip title={t('buttons.edit')} placement='top' arrow>
                      <IconButton
                        size='small'
                        className={styles.editButton}
                        onClick={() => detailsProps.setPropertiesEditMode(true)}
                      >
                        <BrushIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
                <Box
                  className={styles.tabContent}
                  sx={{ height: `calc(100vh - 205px)`, overflow: dataFetching ? 'hidden' : 'auto' }}
                >
                  <DocumentDetailsBlock
                    {...detailsProps}
                    loadingData={dataFetching}
                    setCategory={detailsProps.setCategory}
                    profileLabelsArray={detailsProps.profileLabelsArray(detailsProps.category)}
                    workflowsArray={detailsProps.workflowsArray()}
                    categoryArray={detailsProps.categorySelectValues}
                    typeLabels={detailsProps.getTypeLabels()}
                  />
                </Box>
                {dataFetching && <ProgressOverlay className={'position-absolute'} />}
              </Grid>
              <Grid item xs={6.9} sx={{ mt: -2 }}>
                <Box sx={{ height: `calc(100vh - 128px)` }} className={styles.gridItemBoxShadow}>
                  <AISummaryDetails
                    downloadDocument={() => {
                      if (detailsProps.documentDetails.contract_generation) {
                        setDocumentForDownloading(detailsProps.documentDetails);
                        setFileNameForDownloading(detailsProps.documentDetails.name);
                        setFileTypeModalOpen(true);
                      } else {
                        if (
                          detailsProps?.documentDetails.document_upload_html.length > 0 ||
                          detailsProps?.documentUploadLatestHTMLId
                        ) {
                          setDocumentForDownloading(detailsProps.documentDetails);
                          setFileTypeModalOpen(true);
                        } else {
                          downloadDocumentUploadItem(
                            detailsProps.documentDetails.id,
                            detailsProps.documentDetails.name
                          );
                        }
                      }
                    }}
                    signDocument={() => {
                      setDocumentToSign(detailsProps.documentDetails);
                      if (
                        ('document_upload_html' in detailsProps.documentDetails &&
                          detailsProps.documentDetails.document_upload_html.length > 0) ||
                        detailsProps?.documentUploadLatestHTMLId
                      ) {
                        setSigningDialogOpen(true);
                      } else {
                        handleSignOriginalDocument(detailsProps.documentDetails);
                      }
                    }}
                    deleteDocument={() => {
                      setDocumentToDelete(detailsProps.documentDetails);
                      setDeleteMessageModalOpen(true);
                    }}
                    editDocument={(_) => {
                      handleTabChange(_, 1);
                      navigateToDocumentPageTab(
                        detailsProps.documentDetails,
                        1,
                        false,
                        false,
                        true
                      );
                      if (
                        detailsProps?.documentDetails &&
                        detailsProps?.documentDetails?.du_filetype &&
                        detailsProps?.documentDetails?.du_filetype[0] === 'pdf' &&
                        detailsProps?.documentDetails?.document_upload_html.length === 0 &&
                        !detailsProps?.documentUploadLatestHTMLId
                      ) {
                        detailsProps.setPDFDocHTMLCreationAllowed(true);
                      }
                    }}
                    createTemplate={() => {
                      handleTabChange(null, 1);
                      setTemplateCreationEnabled(true);
                      if (
                        detailsProps?.documentDetails &&
                        detailsProps?.documentDetails?.du_filetype &&
                        detailsProps?.documentDetails?.du_filetype[0] === 'pdf' &&
                        detailsProps?.documentDetails?.document_upload_html.length === 0 &&
                        !detailsProps?.documentUploadLatestHTMLId
                      ) {
                        detailsProps.setPDFDocHTMLCreationAllowed(true);
                      }
                    }}
                    doc={detailsProps.documentDetails}
                    pendingStatus={detailsProps.loadingData}
                    allowGettingAISummary={detailsProps ? detailsProps.htmlRetrieved : false}
                  />
                </Box>
              </Grid>
            </>
          ) : (
            <Grid item xs={12} className={styles.gridItem} sx={{ ml: 2 }}>
              <Tabs value={activeTab} onChange={handleTabChange} className='tabs-document-details'>
                <Tab label={t('tabs.documentDetails')} className={styles.tab} />
                <Tab
                  label={t('tabs.documentContent')}
                  className={`${styles.tab} ${activeTab === 1 ? styles.activeTab : ''}`}
                />
              </Tabs>
              <Box className={styles.tabContent}>
                <DocumentContent
                  documentHTML={documentHTML}
                  contentEditable={contentEditable}
                  generateAISummary={() => {
                    handleTabChange(null, 0);
                  }}
                  downloadDocument={() => {
                    if ('contract_generation' in detailsProps.documentDetails) {
                      setDocumentForDownloading(detailsProps.documentDetails);
                      setFileNameForDownloading(detailsProps.documentDetails.name);
                      setFileTypeModalOpen(true);
                    } else {
                      if (
                        detailsProps.documentDetails.document_upload_html.length > 0 ||
                        detailsProps.documentUploadLatestHTMLId
                      ) {
                        setDocumentForDownloading(detailsProps.documentDetails);
                        setFileTypeModalOpen(true);
                      } else {
                        downloadDocumentUploadItem(
                          detailsProps.documentDetails.id,
                          detailsProps.documentDetails.name
                        );
                      }
                    }
                  }}
                  signDocument={() => {
                    setDocumentToSign(detailsProps.documentDetails);
                    if (
                      ('document_upload_html' in detailsProps.documentDetails &&
                        detailsProps.documentDetails.document_upload_html.length > 0) ||
                      detailsProps.documentUploadLatestHTMLId
                    ) {
                      setSigningDialogOpen(true);
                    } else {
                      handleSignOriginalDocument(detailsProps.documentDetails);
                    }
                  }}
                  deleteDocument={() => {
                    setDocumentToDelete(detailsProps.documentDetails);
                    setDeleteMessageModalOpen(true);
                  }}
                  editDocument={() => {
                    setContentEditable(true);
                    if (
                      detailsProps?.documentDetails &&
                      detailsProps?.documentDetails?.du_filetype &&
                      detailsProps?.documentDetails?.du_filetype[0] === 'pdf' &&
                      detailsProps?.documentDetails?.document_upload_html.length === 0 &&
                      !detailsProps?.documentUploadLatestHTMLId
                    ) {
                      detailsProps.setPDFDocHTMLCreationAllowed(true);
                    }
                  }}
                  saveDocument={saveDocumentHTML}
                  setDocumentHTML={setDocumentHTML}
                  createTemplate={() => {
                    setTemplateCreationEnabled(true);
                    if (
                      detailsProps?.documentDetails &&
                      detailsProps?.documentDetails?.du_filetype &&
                      detailsProps?.documentDetails?.du_filetype[0] === 'pdf' &&
                      detailsProps?.documentDetails?.document_upload_html.length === 0 &&
                      !detailsProps?.documentUploadLatestHTMLId
                    ) {
                      detailsProps.setPDFDocHTMLCreationAllowed(true);
                    }
                  }}
                  doc={detailsProps.documentDetails}
                  templateCreationEnabled={templateCreationEnabled}
                  templatePlaceholders={template}
                  createTemplateFromDoc={() => creatingTemplate()}
                  goBack={() => {
                    setTemplateCreationEnabled(false);
                    setDocumentTemplate(null);
                    setTemplate([]);
                    if (generateTemplateParam && detailsProps) {
                      navigateToDocumentPageTab(detailsProps.documentDetails, 1);
                    }
                  }}
                  setContentEditable={setContentEditable}
                  showConfirmationModal={showConfirmationModal}
                  setShowConfirmationModal={setShowConfirmationModal}
                  deletePlaceholder={deletePlaceholder}
                  documentHTMLId={
                    !contractGenerated &&
                    detailsProps &&
                    detailsProps.documentDetails &&
                    detailsProps.documentDetails.document_upload_html
                      ? Math[checkedLatestVersion ? 'max' : 'min'](
                          // eslint-disable-next-line no-unsafe-optional-chaining
                          ...detailsProps?.documentDetails?.document_upload_html
                        )
                      : null
                  }
                  addTemplatePlaceholderRow={addTemplatePlaceholderRow}
                />
              </Box>
              {(pendingStatusHTML || templateCreationPending || detailsProps.loadingData) && (
                <ProgressOverlay className='position-absolute' />
              )}
            </Grid>
          )}
        </Grid>
      </Box>
      {signingDialogOpen && (
        <MessageModal
          open={signingDialogOpen}
          modalMessage={t('messages.signingVersionMessage')}
          actionButtonText={t('buttons.signLatestVersion')}
          actionButtonFn={() => handleSignLatestVersion(documentToSign)}
          actionButtonAdditionalText={t('buttons.signOriginalDocument')}
          actionButtonAdditionalFn={() => handleSignOriginalDocument(documentToSign)}
          goBackButtonText={t('buttons.goBack')}
          document={documentToSign}
          onClose={() => {
            setDocumentToSign(null);
            setSigningDialogOpen(false);
          }}
        />
      )}
      {detailsProps.pdfDocHTMLCreationAllowed && (
        <MessageModal
          open={detailsProps.pdfDocHTMLCreationAllowed}
          modalMessage={t('messages.creatingHTMLForPDF')}
          noButtons={true}
          modalContent={
            <Box sx={{ width: '100%', padding: '20px 0 0' }}>
              <ProgressBarFancy initialValue={0} finalValue={100} duration={30000} />
            </Box>
          }
          onClose={() => {
            detailsProps.setPDFDocHTMLCreationAllowed(false);
          }}
        />
      )}
      {detailsProps.errorModalOpen && (
        <MessageModal
          open={detailsProps.errorModalOpen}
          modalMessage={t('messages.firstDocumentConversionFail')}
          actionButtonText={t('buttons.retry')}
          actionButtonFn={() => {
            detailsProps.setErrorModalOpen(false);
            detailsProps.createDocumentHTML(Number(documentId));
          }}
          goBackButtonText={t('buttons.goBack')}
          onClose={() => {
            detailsProps.setErrorModalOpen(false);
          }}
        />
      )}
      {fileTypeModalOpen && documentForDownloading && (
        <FileTypeDownloadModal
          onClose={() => {
            handleCloseFileTypeModal();
          }}
          open={fileTypeModalOpen}
          fileName={fileNameForDownloading}
          fileType={fileTypeForDownloading}
          handleFileTypeChange={setFileTypeForDownloading}
          handleFileNameChange={setFileNameForDownloading}
          document={documentForDownloading}
          handleDownloadContract={() => {
            handleDownloadContract(documentForDownloading.id);
          }}
          handleDownloadDocument={() =>
            downloadDocumentUploadItem(documentForDownloading.id, documentForDownloading.name)
          }
          handleDownloadHTML={() => {
            handleDownloadHTML(
              documentForDownloading?.document_upload_html.length > 0
                ? Math.max(...documentForDownloading.document_upload_html)
                : detailsProps?.documentUploadLatestHTMLId,
              documentForDownloading.name.endsWith('.pdf') ||
                documentForDownloading.name.endsWith('.docx') ||
                documentForDownloading.name.endsWith('.doc')
                ? documentForDownloading.name
                    .replace('.pdf', '')
                    .replace('.docx', '')
                    .replace('.doc', '')
                : documentForDownloading.name,
              documentForDownloading.du_filetype[0]
            );
          }}
        />
      )}
      {deleteMessageModalOpen && (
        <MessageModal
          open={deleteMessageModalOpen}
          modalTitle={t('dialogTitles.deleteDocument')}
          modalMessage={t('messages.deleteDocumentDialog')}
          actionButtonText={t('buttons.goBack')}
          actionButtonFn={() => {
            setDocumentToDelete(null);
            setDeleteMessageModalOpen(false);
          }}
          goBackButtonText={t('buttons.delete')}
          document={documentToDelete}
          onClose={() => {
            handleDeleteDocument();
          }}
        />
      )}
      {(detailsProps.pendingStatus ||
        downloadPendingStatus ||
        pendingStatusSigning ||
        documentDeletePendingStatus) && <ProgressOverlay />}
    </>
  );
};

export default DocumentDetails;
